/**
 * Parse API error bodies (template envelope + flat Nest shapes).
 *  */

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function normalizeMessageField(msg: unknown): string | null {
  if (typeof msg === 'string') {
    const t = msg.trim();
    return t.length > 0 ? t : null;
  }
  if (!Array.isArray(msg)) return null;
  const parts = msg
    .filter((x): x is string => typeof x === 'string')
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join('; ') : null;
}

function responseDataFromError(error: unknown): unknown {
  if (!isRecord(error)) return undefined;
  const resp = error.response;
  if (!isRecord(resp)) return undefined;
  return resp.data;
}

function statusFromError(error: unknown): number | undefined {
  if (!isRecord(error)) return undefined;
  const resp = error.response;
  if (!isRecord(resp)) return undefined;
  return typeof resp.status === 'number' ? resp.status : undefined;
}

/**
 * User-facing text from API/axios errors. Prefer backend body over generic Axios text.
 */
export function getApiErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  const data = responseDataFromError(error);
  if (isRecord(data)) {
    const nested = data.error;
    if (isRecord(nested)) {
      const fromEnvelope = normalizeMessageField(nested.message);
      if (fromEnvelope) return fromEnvelope;
    }
    const fromFlat = normalizeMessageField(data.message);
    if (fromFlat) return fromFlat;
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  if (isRecord(error) && typeof error.message === 'string') {
    const t = error.message.trim();
    if (t) return t;
  }
  return fallback;
}

/**
 * For auth forms: surface explicit policy blocks the user must act on — a VPN/
 * proxy block or registration-disabled (403) and rate limiting (429) — but keep
 * a generic message for invalid credentials so we don't aid account enumeration.
 */
export function getAuthErrorMessage(error: unknown, fallback: string): string {
  const status = statusFromError(error);
  if (status === 403 || status === 429) return getApiErrorMessage(error, fallback);
  return fallback;
}
